
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Search, Star, Filter, MapPin, Users, Phone, Globe, Mail, Plus, 
  Calendar, CircleAlert, AlertTriangle 
} from 'lucide-react';
import { mockVenues } from './mockGigData';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';

const VenueDirectory: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string | null>(null);
  const [showInquiryDialog, setShowInquiryDialog] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<any>(null);
  
  const venueTypes = ['All', 'Club', 'Bar', 'Festival', 'Concert Hall', 'Private Event'];
  
  const filteredVenues = mockVenues.filter(venue => {
    const matchesSearch = venue.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          venue.city.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = !filterType || filterType === 'All' || venue.type === filterType;
    
    return matchesSearch && matchesType;
  });

  const handleBookInquiry = (venue: any) => {
    setSelectedVenue(venue);
    setShowInquiryDialog(true);
  };
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-grow">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search venues by name or location..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="flex flex-wrap gap-2">
          {venueTypes.map(type => (
            <Badge
              key={type}
              variant={filterType === type || (type === 'All' && !filterType) ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => setFilterType(type === 'All' ? null : type)}
            >
              {type}
            </Badge>
          ))}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredVenues.map(venue => (
          <Card key={venue.id} className="overflow-hidden">
            <div className="relative h-40 bg-muted">
              <img 
                src={venue.image || "https://source.unsplash.com/random/?venue"} 
                alt={venue.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-2 right-2">
                <Badge variant={venue.played ? 'soundcharts' : 'outline'}>
                  {venue.played ? 'Played Here' : 'New Venue'}
                </Badge>
              </div>
            </div>
            
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <CardTitle>{venue.name}</CardTitle>
                <div className="flex items-center">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star 
                      key={i}
                      className={`h-4 w-4 ${i < venue.rating ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}`}
                    />
                  ))}
                </div>
              </div>
              <CardDescription className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {venue.city}, {venue.state}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="pb-4">
              <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                <div className="flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>Capacity: {venue.capacity}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge variant="outline">{venue.type}</Badge>
                </div>
              </div>
              
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" className="gap-1">
                  <Phone className="h-3.5 w-3.5" />
                  <span className="sr-only md:not-sr-only">Contact</span>
                </Button>
                <Button variant="outline" size="sm" className="gap-1">
                  <Globe className="h-3.5 w-3.5" />
                  <span className="sr-only md:not-sr-only">Website</span>
                </Button>
                <Button variant="outline" size="sm" className="gap-1">
                  <Mail className="h-3.5 w-3.5" />
                  <span className="sr-only md:not-sr-only">Email</span>
                </Button>
                <Button 
                  variant="default" 
                  size="sm" 
                  className="gap-1 ml-auto"
                  onClick={() => handleBookInquiry(venue)}
                >
                  <Calendar className="h-3.5 w-3.5" />
                  <span className="sr-only md:not-sr-only">Submit Inquiry</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {filteredVenues.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No venues found matching your search criteria.</p>
          <Button variant="outline" className="mt-4">
            <Plus className="h-4 w-4 mr-2" />
            Add New Venue
          </Button>
        </div>
      )}

      {/* Venue Booking Inquiry Dialog */}
      <Dialog open={showInquiryDialog} onOpenChange={setShowInquiryDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Submit Booking Inquiry</DialogTitle>
            <DialogDescription>
              {selectedVenue ? `Request a booking at ${selectedVenue.name} in ${selectedVenue.city}` : 'Request a booking'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-muted/50 rounded-md p-3 text-sm flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Important: This is only an inquiry</p>
                <p className="text-muted-foreground mt-1">
                  This will not book the venue automatically. The venue will receive your inquiry and respond with availability, terms, and next steps.
                </p>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <p className="text-sm font-medium">What happens next?</p>
              <ol className="space-y-2 text-sm text-muted-foreground">
                <li className="flex gap-2">
                  <span className="bg-primary/10 text-primary rounded-full h-5 w-5 flex items-center justify-center flex-shrink-0">1</span>
                  <span>Your inquiry will be sent to the venue's booking manager</span>
                </li>
                <li className="flex gap-2">
                  <span className="bg-primary/10 text-primary rounded-full h-5 w-5 flex items-center justify-center flex-shrink-0">2</span>
                  <span>You'll receive confirmation when they review your inquiry</span>
                </li>
                <li className="flex gap-2">
                  <span className="bg-primary/10 text-primary rounded-full h-5 w-5 flex items-center justify-center flex-shrink-0">3</span>
                  <span>If interested, they'll send availability and terms to review</span>
                </li>
                <li className="flex gap-2">
                  <span className="bg-primary/10 text-primary rounded-full h-5 w-5 flex items-center justify-center flex-shrink-0">4</span>
                  <span>You'll need to negotiate and finalize terms before booking</span>
                </li>
              </ol>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowInquiryDialog(false)}>Cancel</Button>
            <Button 
              onClick={() => {
                // In a real app, we would send the inquiry
                setShowInquiryDialog(false);
              }}
            >
              Submit Inquiry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VenueDirectory;
